import {
  type ExamCourseSettings,
  useGetAllExamCoursesWithAvailableQSetsAndExamCenterCoursesQuery,
} from '@exam-center/graphql';
import { DataGrid, Flex, DescriptionOutlinedIcon, Text } from '@shared/components';
import { formatSecondsToHoursMinutes } from '@shared/utils';
import { useMemo } from 'react';

import { columns } from './columns';

function ExamDistributionAvailableQsetDataGridNoRowsOverlay() {
  return (
    <Flex
      sx={(theme) => ({
        py: theme.spacing(4),
        gap: theme.spacing(4),
        flexDirection: 'column',
        alignItems: 'center',
      })}
    >
      <DescriptionOutlinedIcon
        sx={(theme) => ({ fontSize: '48px', color: theme.palette.grey[400] })}
      />
      <Text variant='h5'>ยังไม่ได้มีหลักสูตร</Text>
    </Flex>
  );
}

function renderDurationCell(settings: Pick<ExamCourseSettings, 'examDuration'>) {
  if (!settings?.examDuration?.sessions.length) {
    return <Text>-</Text>;
  }

  return (
    <Flex sx={{ flexDirection: 'column' }}>
      {settings?.examDuration?.sessions.map((session) => (
        <Text key={session.order}>
          ช่วงที่ {session.order}: {formatSecondsToHoursMinutes(session.value, 'SHORT_TEXT')}
        </Text>
      ))}
    </Flex>
  );
}

function sortByExamId(a: { examId: number }, b: { examId: number }) {
  return a.examId - b.examId;
}

export default function ExamDistributionAvailableQSetList() {
  const { data: examCoursesData, loading: isLoading } =
    useGetAllExamCoursesWithAvailableQSetsAndExamCenterCoursesQuery();

  const row = useMemo(() => {
    return (
      examCoursesData?.ecGetAllExamCoursesWithAvailableQSetsAndExamCenterCourses?.examCoursesWithAvailableQSetsAndExamCenterCourses
        ?.map((item) => ({
          examId: item.examId,
          code: item.code,
          name: item.name,
          duration: renderDurationCell(item.settings),
          availableQSets: item.availableQSets.map((qset) => qset.code),
          availableQSetCount: item.availableQSets.length,
          examCenterCourses: item.examCenterCourses.map((course) => course.examCenterCode),
        }))
        .sort(sortByExamId) ?? []
    );
  }, [examCoursesData]);

  return (
    <DataGrid
      rows={row}
      getRowId={(row) => row.examId}
      columns={columns}
      disableColumnMenu
      hideFooter
      getRowHeight={() => 'auto'}
      loading={isLoading}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: theme.palette.background.paper,
        minHeight: '100%',
        '.MuiDataGrid-cell': {
          p: theme.spacing(4),
          cursor: 'default',
          height: 'auto',
          alignItems: 'flex-start',
        },
        '.MuiDataGrid-columnHeaders': {
          backgroundColor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          cursor: 'default',
        },
        '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          cursor: 'default',
        },
        '.MuiDataGrid-row:hover': {
          cursor: 'default',
        },
        '& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered': {
          cursor: 'default',
        },
      })}
      slots={{
        noRowsOverlay: ExamDistributionAvailableQsetDataGridNoRowsOverlay,
      }}
    />
  );
}
